"use client";

import React, { useEffect, useState } from "react";
import { Layers, ShieldCheck, SlidersHorizontal, ArrowRight, FileText, CircleCheck as CheckCircle2, Activity } from "lucide-react";
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
interface SpecItem {
  labelEn: string;
  labelZh: string;
  valueEn: string;
  valueZh: string;
  subEn: string;
  subZh: string;
}
const keySpecs: SpecItem[] = [{
  labelEn: "Slot Aperture Range",
  labelZh: "缝隙精度范围",
  valueEn: "20 µm – 10 mm",
  valueZh: "20 µm – 10 mm",
  subEn: "Aperture Range",
  subZh: "开孔精度"
}, {
  labelEn: "Standard Alloys",
  labelZh: "常用材质",
  valueEn: "304 / 316L / Duplex",
  valueZh: "304 / 316L / 双相钢",
  subEn: "Standard Alloys",
  subZh: "常规材质"
}, {
  labelEn: "Profile Wire Types",
  labelZh: "常用型线",
  valueEn: "12S to 42S",
  valueZh: "12S 至 42S 系列",
  subEn: "Profile Wires",
  subZh: "线型系列"
}, {
  labelEn: "Panel Geometry",
  labelZh: "结构形式",
  valueEn: "Flat & Curved DSM",
  valueZh: "平面 & 弧形DSM",
  subEn: "Form Factors",
  subZh: "结构形态"
}];
const engineeringFeaturesData = {
  en: ["Custom perimeter framing: bar-flanged, stepped, or pre-drilled", "Continuous non-clogging V-wire surface with high open area", "100% optical slot aperture verification and factory inspection", "Direct mill test reports (MTR) for alloy compliance certification"],
  zh: ["支持边框定制：加固边条、折弯边、预留安装螺栓孔", "连续V型线自清自洁缝隙，开孔率高，杜绝颗粒卡堵", "100% 光学缝隙检测与全检控制，保障分离精度", "提供材质质保书 (MTR) 与合金成分合规证明"]
};
const panelTypes = [{
  id: "flat",
  titleEn: "Flat Wedge Wire Panels",
  titleZh: "平面条缝筛板",
  subtitleEn: "Vibrating decks, static dewatering sieves & underdrain floors",
  subtitleZh: "振动筛板、静态脱水筛与过滤底板",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/flat_wedge_wire_panel.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/flat_wedge_wire_panel.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/flat_wedge_wire_panel.png-wvc-sizes",
  badgeEn: "0° Planar Geometry",
  badgeZh: "0° 平面几何结构",
  features: ["Rigid welded cross-rod framing for high vibration resistance", "Continuous non-clogging V-wire surface with high open area", "Custom perimeter framing: bar-flanged, stepped, or bolt-hole pre-drilled"],
  featuresZh: ["高强度支撑条电阻焊接，具备优异抗振抗冲击性能", "连续V型线自清自洁缝隙，开孔率高，杜绝颗粒卡堵", "支持边框定制：加固边条、折弯边、预留安装螺栓孔"]
}, {
  id: "curved",
  titleEn: "Curved & Sieve Bend Panels",
  titleZh: "弧形条缝筛板 / 水力筛板",
  subtitleEn: "DSM static gravity flow sieves & centrifuge screening baskets",
  subtitleZh: "重力弧形筛、脱水水力筛及离心机分级弧板",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/curved_wedge_wire_screen.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/curved_wedge_wire_screen.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/curved_wedge_wire_screen.png-wvc-sizes",
  badgeEn: "Radius R500–R2500 mm",
  badgeZh: "弧度半径 R500–R2500 mm",
  features: ["Precision curved radii (45°, 60°, 90°, 120° or custom arc profiles)", "Cross-flow shearing action maximizing slurry dewatering efficiency", "Interchangeable standard dimensions for OEM screening housings"],
  featuresZh: ["精确弧度成型 (45°、60°、90°、120°及各类非标半径定制)", "横向切向剪切脱水，显著提高重力固液分离与矿浆处理通量", "通用化标准安装规格，适配主流进口与国产筛机壳体"]
}];
export default function PanelHero() {
  const prefersReducedMotion = useReducedMotion();
  const [lang, setLang] = useState<"en" | "zh">("en");
  const [activeTab, setActiveTab] = useState("flat");
  useEffect(() => {
    try {
      const saved = localStorage.getItem("mf_lang");
      if (saved === "zh" || saved === "en") {
        setLang(saved);
      }
    } catch {
      // Ignore localStorage errors in restricted environments
    }
    const handleCustomChange = (e: Event) => {
      const customEvent = e as CustomEvent<{
        lang?: "en" | "zh";
      }>;
      if (customEvent.detail?.lang) {
        setLang(customEvent.detail.lang);
      } else {
        const current = localStorage.getItem("mf_lang");
        if (current === "zh" || current === "en") {
          setLang(current);
        }
      }
    };
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === "mf_lang" && (e.newValue === "zh" || e.newValue === "en")) {
        setLang(e.newValue);
      }
    };
    window.addEventListener("mf-language-change", handleCustomChange);
    window.addEventListener("storage", handleStorageChange);
    return () => {
      window.removeEventListener("mf-language-change", handleCustomChange);
      window.removeEventListener("storage", handleStorageChange);
    };
  }, []);
  const isZh = lang === "zh";
  const engineeringFeatures = isZh ? engineeringFeaturesData.zh : engineeringFeaturesData.en;
  const currentPanel = panelTypes.find(p => p.id === activeTab) || panelTypes[0];
  return <section id="wedgewirepanelhero" data-nav="light" className="relative overflow-hidden bg-background py-20 md:py-28 lg:py-32 border-b border-border">
      {/* Hairline Grid & Wire Weave Motif Background matching Screentubes */}
      <div className="pointer-events-none absolute inset-0 opacity-40 select-none" aria-hidden="true">
        <div className="absolute inset-0 bg-[linear-gradient(to_right,hsl(214_16%_87%/0.5)_1px,transparent_1px),linear-gradient(to_bottom,hsl(214_16%_87%/0.5)_1px,transparent_1px)] bg-[size:40px_40px] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_40%,#000_70%,transparent_100%)]" />
        <div className="absolute -top-32 right-0 size-96 rounded-3xl bg-accent/20 blur-3xl" />
        <div className="absolute -bottom-24 left-1/4 size-80 rounded-3xl bg-secondary/50 blur-3xl" />
      </div>

      <div className="container relative mx-auto px-4 sm:px-6 lg:px-8">
        {/* Main 2-Column Grid */}
        <div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-12 lg:gap-10">
          {/* Left Column (7 Columns on lg) */}
          <motion.div initial={prefersReducedMotion ? {
          opacity: 1
        } : {
          opacity: 0,
          y: 16
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          ease: "easeOut"
        }} className="lg:col-span-7 flex flex-col items-start">
            {/* Technical Badges */}
            <div className="mb-5 flex flex-wrap items-center gap-2">
              <Badge variant="outline" className="rounded-md border-border bg-card px-3 py-1 text-xs font-mono tracking-tight text-foreground shadow-2xs">
                <ShieldCheck className="mr-1.5 size-3.5 text-primary" />
                {isZh ? "精密固液分离介质" : "Precision Filtration Media"}
              </Badge>
              <Badge variant="secondary" className="rounded-md px-2.5 py-1 text-xs font-mono text-secondary-foreground">
                {isZh ? "MF-WWP 系列 (平面 / 弧形筛板)" : "Series MF-WWP (Flat / Curved)"}
              </Badge>
            </div>

            {/* Main Headline */}
            <h1 className="font-default text-balance text-3xl font-bold tracking-tight text-foreground sm:text-4xl md:text-5xl lg:text-[clamp(2.5rem,1.8rem+3.2vw,4.25rem)] lg:leading-[1.08]">
              {isZh ? <>
                  平面与弧形 <br className="hidden sm:inline" />
                  <span className="text-primary">条缝筛板系统</span>
                </> : <>
                  Flat & Curved <br className="hidden sm:inline" />
                  <span className="text-primary">Wedge Wire Panels</span>
                </>}
            </h1>

            {/* Lead Paragraph */}
            <p className="mt-6 max-w-2xl text-base leading-relaxed text-muted-foreground sm:text-lg">
              {isZh ? "专为严苛工况脱水、静态筛分与分级设计。MutualFilter 生产全焊接平面筛板与弧形水力筛（DSM筛板），缝隙开孔精度从 20 微米到 10 毫米，具备高通量与抗堵塞特性。" : "Engineered for heavy-duty dewatering, static screening, and sizing. MutualFilter manufactures flat screen decks and curved DSM sieve bend panels with continuous welded slot profiles from 20 microns to 10 mm."}
            </p>

            {/* CTAs */}
            <div className="mt-8 flex w-full flex-col gap-3 sm:w-auto sm:flex-row sm:items-center">
              <Button size="lg" asChild className="h-12 px-6 text-sm font-semibold shadow-sm transition-colors">
                <Link to="/RequestAQuotePage" className="inline-flex items-center gap-2">
                  <span>
                    {isZh ? "获取定制规格技术报价" : "Request a quote"}
                  </span>
                  <ArrowRight className="size-4" />
                </Link>
              </Button>

              <Button variant="outline" size="lg" asChild className="h-12 px-6 text-sm font-semibold transition-colors">
                <Link to="/ProductSelectionPage" className="inline-flex items-center gap-2">
                  <FileText className="size-4" />
                  <span>
                    {isZh ? "技术选型指南" : "Technical selection guide"}
                  </span>
                </Link>
              </Button>
            </div>

            {/* Engineering Features Checklist Row below top border */}
            <div className="mt-10 grid w-full grid-cols-1 sm:grid-cols-2 gap-3.5 border-t border-border pt-6">
              {engineeringFeatures.map((feature, i) => <div key={i} data-index={i} className="flex items-center gap-2 text-sm text-foreground">
                  <CheckCircle2 className="size-4 text-primary shrink-0" />
                  <span>{feature}</span>
                </div>)}
            </div>
          </motion.div>

          {/* Right Column: Spec Display Card (5 Columns on lg) */}
          <motion.div initial={prefersReducedMotion ? {
          opacity: 1
        } : {
          opacity: 0,
          scale: 0.98
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.7,
          ease: "easeOut",
          delay: 0.1
        }} className="lg:col-span-5">
            <Card className="overflow-hidden rounded-xl border border-border bg-card shadow-lg">
              {/* Card Header Topbar with Tabs */}
              <div className="border-b border-border bg-muted/30 px-4 py-3 sm:px-5">
                <div className="flex items-center justify-between gap-2">
                  <div>
                    <h2 className="text-xs font-bold uppercase tracking-wider text-muted-foreground sm:text-xs">
                      {isZh ? "筛板配置切换" : "Geometry Configuration"}
                    </h2>
                  </div>
                  <Badge variant="secondary" className="font-mono text-[11px] shrink-0">
                    <Activity className="mr-1 size-3 text-primary" />
                    MutualFilter™
                  </Badge>
                </div>

                <Tabs value={activeTab} onValueChange={setActiveTab} className="mt-2.5 w-full">
                  <TabsList className="grid w-full grid-cols-2 bg-muted/80">
                    <TabsTrigger value="flat" className="text-xs data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-sm">
                      {isZh ? "平面筛板 (Flat)" : "Flat Panels"}
                    </TabsTrigger>
                    <TabsTrigger value="curved" className="text-xs data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-sm">
                      {isZh ? "弧形水力筛 (DSM)" : "Curved / Sieve Bends"}
                    </TabsTrigger>
                  </TabsList>
                </Tabs>
              </div>

              {/* Product Visual Container */}
              <div className="relative aspect-4/3 w-full overflow-hidden bg-muted/60">
                <img data-wvc-srcset={currentPanel.srcset} data-wvc-sizes={currentPanel.sizes} alt={isZh ? currentPanel.titleZh : currentPanel.titleEn} loading="lazy" decoding="async" className="h-full w-full object-cover object-center transition-transform duration-500 hover:scale-105" src="https://mutualfilter.com/wp-content/uploads/2026/09/flat-weged-wire-screen.webp" />
                <div className="absolute top-3 left-3 bg-card/90 backdrop-blur-xs border border-border px-3 py-1 rounded-md text-xs font-mono text-foreground flex items-center gap-1.5 shadow-2xs">
                  <Layers className="size-3.5 text-primary" />
                  <span>
                    {isZh ? currentPanel.badgeZh : currentPanel.badgeEn}
                  </span>
                </div>
              </div>

              {/* Technical Specifications Grid inside CardContent */}
              <CardContent className="p-4 sm:p-5">
                <div className="mb-3 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                  {isZh ? "标准制造技术参数 (ASTM / ISO)" : "Standard Manufacturing Parameters"}
                </div>

                <div className="grid grid-cols-2 gap-2.5">
                  {keySpecs.map((spec, idx) => <div key={idx} data-index={idx} className="rounded-md border border-border/60 bg-muted/30 p-2.5 transition-colors hover:bg-muted/60">
                      <div className="text-[11px] font-medium text-muted-foreground uppercase tracking-wider">
                        {isZh ? spec.labelZh : spec.labelEn}
                      </div>
                      <div className="mt-0.5 font-mono text-xs sm:text-sm font-bold text-foreground">
                        {isZh ? spec.valueZh : spec.valueEn}
                      </div>
                      <div className="text-[10px] sm:text-[11px] text-muted-foreground mt-0.5 leading-tight">
                        {isZh ? spec.subZh : spec.subEn}
                      </div>
                    </div>)}
                </div>

                <Separator className="my-4" />

                <div className="flex items-center justify-between text-xs">
                  <div className="flex items-center gap-1.5 text-muted-foreground">
                    <SlidersHorizontal className="size-3.5 text-primary" />
                    <span>
                      {isZh ? "支持定制端部法兰与预留安装孔" : "Custom edge framing & mounting flanges"}
                    </span>
                  </div>
                  <Link to={currentPanel.id === "flat" ? "/FlatPanelScreensPage" : "/WedgeWireScreenPage"} className="font-medium text-primary hover:underline flex items-center gap-1 shrink-0">
                    <span>{isZh ? "工程图纸与选型" : "Engineering drawings"}</span>
                    <ArrowRight className="size-3" />
                  </Link>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </div>
    </section>;
}